import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Diamond, Download, FileText, File as FileIcon, Loader2, ShieldAlert } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function fmtSize(b) {
  b = Number(b || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

export default function DrivePublic() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    axios.get(`${BASE}/api/drive/shared/${token}/info`)
      .then((r) => { setInfo(r.data); setState("ok"); })
      .catch(() => setState("error"));
  }, [token]);

  const src = `${BASE}/api/drive/shared/${token}`;
  const dl = `${BASE}/api/drive/shared/${token}?dl=1`;

  return (
    <div className="min-h-screen bg-[#0A1C30] flex flex-col">
      <header className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
        <Diamond className="w-5 h-5 text-gold fill-[var(--gold)]" />
        <span className="font-extrabold tracking-[0.3em] text-white">SUPERA</span>
        <span className="text-white/40 text-sm ml-2 hidden sm:inline">· Supera Drive</span>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        {state === "loading" && <Loader2 className="w-8 h-8 animate-spin text-gold" />}

        {state === "error" && (
          <div className="text-center text-white/80 max-w-sm" data-testid="drive-public-error">
            <ShieldAlert className="w-14 h-14 mx-auto mb-4 text-gold/80" />
            <h1 className="text-2xl font-extrabold text-white mb-2">Enlace no disponible</h1>
            <p className="text-sm">Este enlace no es válido o el archivo ya no se comparte. Solicita uno nuevo a tu equipo Supera.</p>
          </div>
        )}

        {state === "ok" && info && (
          <div className="w-full max-w-3xl" data-testid="drive-public-view">
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
              <div className="px-6 py-5 border-b border-black/5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h1 className="text-lg font-extrabold text-[#0A1C30] truncate">{info.nombre}</h1>
                  <p className="text-xs text-[#6B7688] capitalize">{info.tipo} · {fmtSize(info.size)}</p>
                </div>
                <a href={dl} className="inline-flex items-center gap-2 bg-[#0A1C30] text-white text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-[#0A1C30]/90 transition-colors flex-shrink-0" data-testid="drive-public-download">
                  <Download className="w-4 h-4" /> Descargar
                </a>
              </div>
              <div className="bg-[#0A1C30]/5 flex items-center justify-center p-3 min-h-[300px]">
                {info.tipo === "imagen" ? (
                  <img src={src} alt={info.nombre} className="max-w-full max-h-[70vh] object-contain rounded-xl" />
                ) : info.tipo === "video" ? (
                  <video src={src} controls playsInline className="max-w-full max-h-[70vh] rounded-xl" />
                ) : info.tipo === "audio" ? (
                  <div className="w-full max-w-lg p-6"><audio src={src} controls className="w-full" /></div>
                ) : info.ext === "pdf" ? (
                  <iframe src={src} title={info.nombre} className="w-full h-[70vh] rounded-xl bg-white" />
                ) : (
                  <div className="text-center py-12 text-[#6B7688]">
                    {info.tipo === "documento" || info.tipo === "texto" ? <FileText className="w-16 h-16 mx-auto mb-4 opacity-40" /> : <FileIcon className="w-16 h-16 mx-auto mb-4 opacity-40" />}
                    <p className="text-sm">Descarga el archivo para verlo en tu dispositivo.</p>
                  </div>
                )}
              </div>
            </div>
            <p className="text-center text-white/40 text-xs mt-6">Compartido de forma segura con Supera Drive · Supera Leadership</p>
          </div>
        )}
      </main>
    </div>
  );
}
